// Lib
import React, { ChangeEvent, ReactElement } from 'react';
import { useSelector } from 'react-redux';

// Selectors
import { isDebugToolbarToolEnabled } from '../../debug/debugStateSelector';

// Components
import DebugGlobalRangeInput from '../../debug/debugPopup/DebugGlobalRangeInput';
import SettingsRow from '../../user/account/accountModal/components/SettingsRow';
import DebugPanel from '../../debug/DebugPanel';

// Constants
import { DEBUG_TOOLS } from '../../debug/store/debugToolConstants';

// Styles
import './MobileKeyboardAnimationDebugPanel.scss';

declare global {
    interface Window {
        keyboard: {
            stickToKeyboard: boolean;

            revealDuration: number;
            revealDelay: number;
            revealControlPoint1x: number;
            revealControlPoint1y: number;
            revealControlPoint2x: number;
            revealControlPoint2y: number;

            hideDuration: number;
            hideDelay: number;
            hideControlPoint1x: number;
            hideControlPoint1y: number;
            hideControlPoint2x: number;
            hideControlPoint2y: number;
        };
    }
}

global.window.keyboard = {
    stickToKeyboard: false,

    revealDuration: 0,
    revealDelay: 0,
    revealControlPoint1x: 0,
    revealControlPoint1y: 0,
    revealControlPoint2x: 0,
    revealControlPoint2y: 0,

    hideDuration: 0,
    hideDelay: 0,
    hideControlPoint1x: 0,
    hideControlPoint1y: 0,
    hideControlPoint2x: 0,
    hideControlPoint2y: 0,
};

const initialiseGlobalKeyboardVariables = () => {
    const bodyComputedStyle = getComputedStyle(global.document.body);

    const getRootPropertyNumericValue = (propertyName: string): number => {
        let value = bodyComputedStyle.getPropertyValue(propertyName) || '0';

        if (value.endsWith('s')) {
            value = value.slice(0, -1);
        }

        return parseFloat(value);
    };

    global.window.keyboard.revealDuration = getRootPropertyNumericValue('--keyboard-animation-reveal-duration');
    global.window.keyboard.revealDelay = getRootPropertyNumericValue('--keyboard-animation-reveal-delay');
    global.window.keyboard.revealControlPoint1x = getRootPropertyNumericValue(
        '--keyboard-animation-reveal-control-point-1-x',
    );
    global.window.keyboard.revealControlPoint1y = getRootPropertyNumericValue(
        '--keyboard-animation-reveal-control-point-1-y',
    );
    global.window.keyboard.revealControlPoint2x = getRootPropertyNumericValue(
        '--keyboard-animation-reveal-control-point-2-x',
    );
    global.window.keyboard.revealControlPoint2y = getRootPropertyNumericValue(
        '--keyboard-animation-reveal-control-point-2-y',
    );
    global.window.keyboard.hideDuration = getRootPropertyNumericValue('--keyboard-animation-hide-duration');
    global.window.keyboard.hideDelay = getRootPropertyNumericValue('--keyboard-animation-hide-delay');
    global.window.keyboard.hideControlPoint1x = getRootPropertyNumericValue(
        '--keyboard-animation-hide-control-point-1-x',
    );
    global.window.keyboard.hideControlPoint1y = getRootPropertyNumericValue(
        '--keyboard-animation-hide-control-point-1-y',
    );
    global.window.keyboard.hideControlPoint2x = getRootPropertyNumericValue(
        '--keyboard-animation-hide-control-point-2-x',
    );
    global.window.keyboard.hideControlPoint2y = getRootPropertyNumericValue(
        '--keyboard-animation-hide-control-point-2-y',
    );
};

interface MobileKeyboardAnimationDebugPanelProps {
    mobileWorkspaceRef: React.RefObject<HTMLDivElement>;
}

const MobileKeyboardAnimationDebugPanel = ({
    mobileWorkspaceRef,
}: MobileKeyboardAnimationDebugPanelProps): ReactElement | null => {
    React.useLayoutEffect(() => {
        initialiseGlobalKeyboardVariables();
    }, []);

    const isKeyboardAnimationEnabled = useSelector(isDebugToolbarToolEnabled(DEBUG_TOOLS.MOBILE_KEYBOARD_ANIMATION));

    if (!isKeyboardAnimationEnabled) return null;

    const setKeyboardAnimationVariable = (variableName: string, value: string) => {
        if (!mobileWorkspaceRef.current) return;

        mobileWorkspaceRef.current.style.setProperty(`--keyboard-animation-${variableName}`, value);
    };

    const onRevealDurationChange = (e: ChangeEvent<HTMLInputElement>) =>
        setKeyboardAnimationVariable('reveal-duration', `${e.target.value}s`);
    const onRevealDelayChange = (e: ChangeEvent<HTMLInputElement>) =>
        setKeyboardAnimationVariable('reveal-delay', `${e.target.value}s`);

    const onRevealControlPoint1xChange = (e: ChangeEvent<HTMLInputElement>) =>
        setKeyboardAnimationVariable('reveal-control-point-1-x', e.target.value);
    const onRevealControlPoint1yChange = (e: ChangeEvent<HTMLInputElement>) =>
        setKeyboardAnimationVariable('reveal-control-point-1-y', e.target.value);
    const onRevealControlPoint2xChange = (e: ChangeEvent<HTMLInputElement>) =>
        setKeyboardAnimationVariable('reveal-control-point-2-x', e.target.value);
    const onRevealControlPoint2yChange = (e: ChangeEvent<HTMLInputElement>) =>
        setKeyboardAnimationVariable('reveal-control-point-2-y', e.target.value);

    const onHideDurationChange = (e: ChangeEvent<HTMLInputElement>) =>
        setKeyboardAnimationVariable('hide-duration', `${e.target.value}s`);
    const onHideDelayChange = (e: ChangeEvent<HTMLInputElement>) =>
        setKeyboardAnimationVariable('hide-delay', `${e.target.value}s`);

    const onHideControlPoint1xChange = (e: ChangeEvent<HTMLInputElement>) =>
        setKeyboardAnimationVariable('hide-control-point-1-x', e.target.value);
    const onHideControlPoint1yChange = (e: ChangeEvent<HTMLInputElement>) =>
        setKeyboardAnimationVariable('hide-control-point-1-y', e.target.value);
    const onHideControlPoint2xChange = (e: ChangeEvent<HTMLInputElement>) =>
        setKeyboardAnimationVariable('hide-control-point-2-x', e.target.value);
    const onHideControlPoint2yChange = (e: ChangeEvent<HTMLInputElement>) =>
        setKeyboardAnimationVariable('hide-control-point-2-y', e.target.value);

    return (
        <div className="MobileKeyboardAnimationDebugPanel DebugPopup">
            <DebugPanel toolName={DEBUG_TOOLS.MOBILE_KEYBOARD_ANIMATION}>
                <h1>Keyboard animation</h1>

                <details>
                    <summary>Reveal animation</summary>

                    <SettingsRow label="Duration" details="">
                        <DebugGlobalRangeInput
                            globalObject={global.window.keyboard}
                            min={0.15}
                            max={0.75}
                            step={0.01}
                            label="Reveal duration"
                            prop="revealDuration"
                            onChange={onRevealDurationChange}
                        />
                    </SettingsRow>

                    <SettingsRow label="Delay" details="">
                        <DebugGlobalRangeInput
                            globalObject={global.window.keyboard}
                            min={0}
                            max={0.7}
                            step={0.01}
                            label="Reveal delay"
                            prop="revealDelay"
                            onChange={onRevealDelayChange}
                        />
                    </SettingsRow>
                </details>

                <details>
                    <summary>Reveal Control point</summary>

                    <SettingsRow label="Control point 1 x" details="">
                        <DebugGlobalRangeInput
                            globalObject={global.window.keyboard}
                            min={0}
                            max={1.1}
                            step={0.01}
                            label="Control point 1 x"
                            prop="revealControlPoint1x"
                            onChange={onRevealControlPoint1xChange}
                        />
                    </SettingsRow>
                    <SettingsRow label="Control point 1 y" details="">
                        <DebugGlobalRangeInput
                            globalObject={global.window.keyboard}
                            min={0}
                            max={1.1}
                            step={0.01}
                            label="Control point 1 y"
                            prop="revealControlPoint1y"
                            onChange={onRevealControlPoint1yChange}
                        />
                    </SettingsRow>
                    <SettingsRow label="Control point 2 x" details="">
                        <DebugGlobalRangeInput
                            globalObject={global.window.keyboard}
                            min={0}
                            max={1.1}
                            step={0.01}
                            label="Control point 2 x"
                            prop="revealControlPoint2x"
                            onChange={onRevealControlPoint2xChange}
                        />
                    </SettingsRow>
                    <SettingsRow label="Control point 2 y" details="">
                        <DebugGlobalRangeInput
                            globalObject={global.window.keyboard}
                            min={0}
                            max={1.1}
                            step={0.01}
                            label="Control point 2 y"
                            prop="revealControlPoint2y"
                            onChange={onRevealControlPoint2yChange}
                        />
                    </SettingsRow>
                </details>

                <details>
                    <summary>Hide animation</summary>
                    <SettingsRow label="Duration" details="">
                        <DebugGlobalRangeInput
                            globalObject={global.window.keyboard}
                            min={0}
                            max={0.75}
                            step={0.01}
                            label="Hide duration"
                            prop="hideDuration"
                            onChange={onHideDurationChange}
                        />
                    </SettingsRow>

                    <SettingsRow label="Hide delay" details="">
                        <DebugGlobalRangeInput
                            globalObject={global.window.keyboard}
                            min={0}
                            max={0.4}
                            step={0.01}
                            label="Delay"
                            prop="hideDelay"
                            onChange={onHideDelayChange}
                        />
                    </SettingsRow>
                </details>

                <details>
                    <summary>Hide Control point</summary>

                    <SettingsRow label="Control point 1 x" details="">
                        <DebugGlobalRangeInput
                            globalObject={global.window.keyboard}
                            min={0}
                            max={1.1}
                            step={0.01}
                            label="Control point 1 x"
                            prop="hideControlPoint1x"
                            onChange={onHideControlPoint1xChange}
                        />
                    </SettingsRow>
                    <SettingsRow label="Control point 1 y" details="">
                        <DebugGlobalRangeInput
                            globalObject={global.window.keyboard}
                            min={0}
                            max={1.1}
                            step={0.01}
                            label="Control point 1 y"
                            prop="hideControlPoint1y"
                            onChange={onHideControlPoint1yChange}
                        />
                    </SettingsRow>
                    <SettingsRow label="Control point 2 x" details="">
                        <DebugGlobalRangeInput
                            globalObject={global.window.keyboard}
                            min={0}
                            max={1.1}
                            step={0.01}
                            label="Control point 2 x"
                            prop="hideControlPoint2x"
                            onChange={onHideControlPoint2xChange}
                        />
                    </SettingsRow>
                    <SettingsRow label="Control point 2 y" details="">
                        <DebugGlobalRangeInput
                            globalObject={global.window.keyboard}
                            min={0}
                            max={1.1}
                            step={0.01}
                            label="Control point 2 y"
                            prop="hideControlPoint2y"
                            onChange={onHideControlPoint2yChange}
                        />
                    </SettingsRow>
                </details>
            </DebugPanel>
        </div>
    );
};

export default MobileKeyboardAnimationDebugPanel;
